import * as React from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import {
  Button,  ButtonVariant,
  Masthead,
  MastheadBrand,
  MastheadLogo,
  MastheadMain, MastheadContent,
  MastheadToggle,
  Nav,
  NavExpandable,
  NavItem,
  NavList,
  Page,
  PageSidebar,
  PageSidebarBody,  Popper,
  SkipToContent, Title,
   Toolbar,
    ToolbarContent,
    ToolbarGroup,
    ToolbarItem
} from '@patternfly/react-core';
import { IAppRoute, IAppRouteGroup, routes } from '@app/routes';
import BarsIcon from '@patternfly/react-icons/dist/esm/icons/bars-icon';
import QuestionCircleIcon from '@patternfly/react-icons/dist/esm/icons/question-circle-icon';
import SignOutAltIcon from '@patternfly/react-icons/dist/esm/icons/sign-out-alt-icon';
import { SUPPORT_EMAIL, PARTNER_DISPLAY_NAME, PRODUCT_NAME  } from '../../config/partner';
import { isAuthenticated, signOutUser } from '@app/utils/cognitoAuth';

interface IAppLayout {
  children: React.ReactNode;
}
const AppLayout: React.FunctionComponent<IAppLayout> = ({ children }) => {
  const [sidebarOpen, setSidebarOpen] = React.useState(true);
  const [isUserAuthenticated, setIsUserAuthenticated] = React.useState(false);

  React.useEffect(() => {
    const checkAuth = async () => {
      const auth = await isAuthenticated();
      setIsUserAuthenticated(auth);
    };
    checkAuth();
  }, []);

  const handleSignOut = async () => {
    await signOutUser();
    setIsUserAuthenticated(false);
    window.location.href = '/'; // Redirect to home page after sign out
  };

      const headerToolbar = (
        <Toolbar id="toolbar" isStatic>
          <ToolbarContent>
            <ToolbarGroup
              variant="action-group-plain"
              align={{ default: 'alignEnd' }}
              gap={{ default: 'gapNone', md: 'gapMd' }}
            >
              {isUserAuthenticated && (
                <ToolbarItem>
                  <Button 
                    aria-label="Sign Out" 
                    variant={ButtonVariant.plain} 
                    icon={<SignOutAltIcon />} 
                    onClick={handleSignOut}
                  />
                </ToolbarItem>
              )}
              <ToolbarItem>
                <Button aria-label="Notifications" variant={ButtonVariant.primary}  component="a" href={`mailto:${SUPPORT_EMAIL}?subject=I have a question about ${PRODUCT_NAME}`} >Contact</Button>
              </ToolbarItem>

                <ToolbarItem>
                  <Button aria-label="Docs" variant={ButtonVariant.plain} icon={<QuestionCircleIcon />} component="a" href="/docs"  />
                </ToolbarItem>

               </ToolbarGroup>

          </ToolbarContent>
        </Toolbar>
      );
  const masthead = (
    <Masthead>
      <MastheadMain>
        <MastheadToggle>
          <Button
            icon={<BarsIcon />}
            variant="plain"
            onClick={() => setSidebarOpen(!sidebarOpen)}
            aria-label="Global navigation"
          />
        </MastheadToggle>
        <MastheadBrand data-codemods style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <MastheadLogo data-codemods>
            <img src="/images/DL-Logo.png" alt="Logo" width="40" height="40" />
          </MastheadLogo>
          <Title headingLevel="h1" size="2xl" style={{ color: '#ffffff', margin: 0 }}>{PARTNER_DISPLAY_NAME} {PRODUCT_NAME}</Title>
        </MastheadBrand>
      </MastheadMain> <MastheadContent>{headerToolbar}</MastheadContent>
    </Masthead>
  );

  const location = useLocation();

  const renderNavItem = (route: IAppRoute, index: number) => (
    <NavItem key={`${route.label}-${index}`} id={`${route.label}-${index}`} isActive={route.path === location.pathname}>
      <NavLink to={route.path} end>
        {route.label}
      </NavLink>
    </NavItem>
  );

  const renderNavGroup = (group: IAppRouteGroup, groupIndex: number) => (
    <NavExpandable
      key={`${group.label}-${groupIndex}`}
      id={`${group.label}-${groupIndex}`}
      title={group.label}
      isActive={group.routes.some((route) => route.path === location.pathname)}
    >
      {group.routes.map((route, idx) => route.label && renderNavItem(route, idx))}
    </NavExpandable>
  );

  const Navigation = (
    <Nav id="nav-primary-simple">
      <NavList id="nav-list-simple">
        {routes.map(
          (route, idx) => route.label && (!route.routes ? renderNavItem(route, idx) : renderNavGroup(route, idx)),
        )}
      </NavList>
    </Nav>
  );

  const Sidebar = (
    <PageSidebar>
      <PageSidebarBody>{Navigation}</PageSidebarBody>
    </PageSidebar>
  );

  const pageId = 'primary-app-container';

  const PageSkipToContent = (
    <SkipToContent
      onClick={(event) => {
        event.preventDefault();
        const primaryContentContainer = document.getElementById(pageId);
        primaryContentContainer && primaryContentContainer.focus();
      }}
      href={`#${pageId}`}
    >
      Skip to Content
    </SkipToContent>
  );

  const footer = (
    <div style={{ 
      position: 'fixed', 
      bottom: 0, 
      width: '100%', 
      textAlign: 'center', 
      padding: '1rem',
      backgroundColor: '#f0f0f0',
    }}>
      <NavLink to="/terms" style={{ color: '#0066cc', textDecoration: 'none' }}>
        Terms and Conditions
      </NavLink>
    </div>
  );

  return (
    <>
      <Page
        mainContainerId={pageId}
        masthead={masthead}
        sidebar={sidebarOpen && Sidebar}
        skipToContent={PageSkipToContent}
        style={{ paddingBottom: '3rem' }}
      >
        {children}
      </Page>
      {footer}
    </>
  );
};

export { AppLayout };
